/* src/components/Quotes/QuoteDetailModal.jsx
   -------------------------------------------------------------- */
import React, { useEffect, useState, useMemo } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, Box, Text, Table, Thead, Tbody,
  Tr, Th, Td, Spinner, Center, VStack,
} from "@chakra-ui/react";

import {
  fetchQuoteById, fetchQuoteProducts, fetchPeriodicities, fetchSlas,
} from "../../services/api";

import Num from "../common/Num";          // ⬅️  nuovo import  (../common)

/* ─────────────────────────────────────────────────────────── */
export default function QuoteDetailModal({ isOpen, onClose, quote }) {
  const [loading, setLoading]       = useState(false);
  const [periodicity, setPeriodicity] = useState(null);
  const [products, setProducts]     = useState([]);
  const [slas, setSlas]             = useState([]);

  /* ─────── FETCH header + righe ─────── */
  useEffect(() => {
    if (!isOpen || !quote?.id) return;

    (async () => {
      setLoading(true);
      try {
        const header = (await fetchQuoteById(quote.id)).data;

        const allPeriods = (await fetchPeriodicities()).data;
        setPeriodicity(allPeriods.find(p => p.id === header.periodicity_id) || null);

        setProducts((await fetchQuoteProducts(quote.id)).data);
        setSlas((await fetchSlas()).data);
      } catch (err) {
        console.error("Errore caricamento dettaglio quote", err);
      } finally { setLoading(false); }
    })();
  }, [isOpen, quote]);

  /* ─────── riepilogo economico ─────── */
  const summary = useMemo(() => {
    if (!periodicity) return null;

    const parents  = products.filter(p =>
      p.pivot_id == null || parseFloat(p.total_amount ?? 0) > 0);
    const options  = products.filter(p =>
      p.pivot_id != null ||
      (parseFloat(p.total_amount ?? 0) === 0 && parseFloat(p.last_unit_price ?? 0) > 0));

    let imponibile = 0, sconti = 0, netto = 0, opzioni = 0;

    parents.forEach(p => {
      const listino  = Number(p.total_amount          ?? 0);
      const scontato = Number(p.total_amount_scontato ?? 0);
      imponibile += listino;
      sconti     += listino - scontato;
      netto      += scontato;
    });

    options.forEach(o => {
      opzioni += Number(o.last_unit_price ?? 0) * Number(o.quantity ?? 1);
    });

    const iva    = (netto + opzioni) * 0.22;
    const totale = netto + iva + opzioni;

    return { imponibile, sconti, netto, opzioni, iva, totale };
  }, [products, periodicity]);

  const slaTitle = id => slas.find(s => s.id === id)?.titolo || "—";

  /* ─────── UI ─────── */
  return (
    <Modal isOpen={isOpen} onClose={onClose}
           size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="90vw" w="90vw">
        <ModalHeader>Dettagli Preventivo #{quote?.id}</ModalHeader>
        <ModalCloseButton />

        <ModalBody maxH="40vh" overflowY="auto" pb={4}>
          {loading ? (
            <Center py={10}><Spinner size="lg" /></Center>
          ) : (
            <>
              {/* ---------- riepilogo ---------- */}
              {summary && (
                <Box mb={6}>
                  <VStack align="start" spacing={1} fontSize="sm">
                    <Text>Imponibile:         <Num value={summary.imponibile} fmt="€" /></Text>
                    <Text>Sconti:             <Num value={summary.sconti}     fmt="€" /></Text>
                    <Text>Netto:              <Num value={summary.netto}      fmt="€" /></Text>
                    <Text>Totale Opzioni:     <Num value={summary.opzioni}    fmt="€" /></Text>
                    <Text>IVA (22%):          <Num value={summary.iva}        fmt="€" /></Text>
                    <Text fontWeight="bold">TOTALE: <Num value={summary.totale} fmt="€" /></Text>
                  </VStack>
                </Box>
              )}

              {/* ---------- tabella prodotti ---------- */}
              <Table variant="striped" size="sm">
                <Thead>
                  <Tr>
                    <Th>Codice</Th><Th>Descrizione</Th>
                    <Th isNumeric>Listino/mese</Th>
                    <Th isNumeric>Prezzo riservato</Th>
                    <Th isNumeric>Q.ta</Th>
                    <Th isNumeric>% sconto</Th>
                    <Th>SLA</Th>
                    <Th isNumeric>Totale listino</Th>
                    <Th isNumeric>Totale riservato</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {products.map(p => {
                    const qty        = Number(p.quantity ?? 1);
                    const listinoU   = Number(p.last_unit_price ?? 0);
                    const riservU    = Number(p.price_reserved  ?? 0);
                    const totList    = listinoU * qty;
                    const totRiserv  = riservU  * qty;

                    return (
                      <Tr key={p.id}>
                        <Td>{p.product_id}</Td>
                        <Td>{p.description || "—"}</Td>
                        <Td isNumeric><Num value={listinoU}  fmt="€" /></Td>
                        <Td isNumeric><Num value={riservU}   fmt="€" /></Td>
                        <Td isNumeric><Num value={qty}       fmt="int" /></Td>
                        <Td isNumeric><Num value={p.discount_percent} fmt="%" /></Td>
                        <Td>{slaTitle(p.sla_id)}</Td>
                        <Td isNumeric><Num value={totList}   fmt="€" /></Td>
                        <Td isNumeric><Num value={totRiserv} fmt="€" /></Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Chiudi</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
